import { z } from "zod/v4";

import { ApiDto, ApiDtoSchema } from "./apis";

export type DefaultIntegrationGlobalSettingsDto = object;

export const IntegrationNameList = [
  "Recharge",
  "StayAi",
  "GrowLTV",
  "OneClickUpsell",
  "Google",
  "Amped",
  "Clarity",
  "Hotjar",
  "Heatmap",
  "CustomApi",
  "Klaviyo",
  "Slack",
  "Amplitude",
  "Heap",
  "Segment",
] as const;
export type IntegrationName = (typeof IntegrationNameList)[number];

export type HeadlessSupportedIntegration = "Heatmap" | "Hotjar" | "Clarity";

export const IntegrationTypeList = [
  "SUBSCRIPTION",
  "UI",
  "ANALYTICS",
  "API",
] as const;
export type IntegrationTypeType = (typeof IntegrationTypeList)[number];

export const BaseIntegrationDtoSchema = z.object({
  id: z.uuid(),
  name: z.enum(IntegrationNameList),
  description: z.string(),
  enabled: z.boolean(),
  globalSettings: z.any(),
  apis: ApiDtoSchema.array().default([]),
});

export const CreateBaseIntegrationDtoSchema = BaseIntegrationDtoSchema.extend({
  id: z.uuid().optional(),
});

export interface BaseIntegrationDto<
  GlobalSettings = object,
  Internal = object,
> {
  id: string;
  name: IntegrationName;
  description: string;
  enabled: boolean;
  globalSettings?: GlobalSettings;
  internal?: Internal;
  apis: ApiDto[];
}

export const IntegrationWithSensitiveFieldsDtoSchema =
  BaseIntegrationDtoSchema.extend({
    accessToken: z.string().nullish(),
    refreshToken: z.string().nullish(),
    clientSecret: z.string().nullish(),
    scope: z.string().nullish(),
    expiresIn: z.number().nullish(),
    expiresAt: z.number().nullish(),
    internal: z.any().nullish(),
    organizationId: z.string().optional(),
  });

export type IntegrationWithSensitiveFieldsDto = z.infer<
  typeof IntegrationWithSensitiveFieldsDtoSchema
>;

export const AvailableIntegrationSchema =
  IntegrationWithSensitiveFieldsDtoSchema.extend({
    cls: z.any(),
    type: z.enum(IntegrationTypeList),
    defaultExperienceSettings: z.any().nullish(),
  });
export type AvailableIntegrationType = z.infer<
  typeof AvailableIntegrationSchema
>;

export interface IgBaseIntegrationLikeObject {
  initializeAfterDom: () => void;
  earlyUpdateDom: () => any;
  lateUpdateDom: () => any;
  name: string;
  enabled: boolean;
}

export interface IgGaIntegrationLikeObject extends IgBaseIntegrationLikeObject {
  getTrackedExperiences: () => Record<string, true>;
  setTrackedExperiences: (gaVariationNames: Record<string, true>) => void;
}

export const CreateIntegrationDtoSchema =
  IntegrationWithSensitiveFieldsDtoSchema.omit({ apis: true }).extend({
    id: z.uuid().optional(),
    organizationId: z.uuid().optional(),
  });

export type CreateIntegrationDto = z.infer<typeof CreateIntegrationDtoSchema>;
